import { guestKey, modelVersions, drainReuseKeys } from '../services/constants';
import { outputColumns } from '../services/model';
import * as actions from './actions';
import { analysisColumnKeys, loadingStates } from './constants';
import initialState from './initialState';

const actionTypes = actions.actionTypes;

const updateValue = (value, prev, state) => {
    if (typeof value === 'function') {
        return value(prev, state);
    }
    return value;
};

/**
 * @typedef {import('./initialState').TStore} TStore
 *
 * @typedef {Object} TAction
 * @property {string} type
 * @property {*} payload
 *
 * @param {TStore} state
 * @param {TAction} action
 * @returns {TStore}
 */
const reducer = (state, action) => {
    const initial = initialState(state.modelVersion);

    switch (action.type) {
        case actionTypes.setKey: {
            const key = action.payload || guestKey;
            return { ...state, key, loadingState: loadingStates.initializing };
        }
        case actionTypes.setStoreKeyInBrowser: {
            const nextValue = action.payload;
            return { ...state, storeKeyInBrowser: nextValue };
        }
        case actionTypes.setConfig: {
            const config = action.payload;
            return { ...state, config };
        }
        case actionTypes.startInitializeStore: {
            const { key: nextKey, modelVersion: nextModelVersion } =
                action.payload;
            let nextState = {
                ...state,
                modelVersion: nextModelVersion || state.modelVersion,
            };
            nextState = reducer(nextState, { type: actionTypes.clearInput });
            nextState = reducer(nextState, {
                type: actionTypes.setConfig,
                payload: null,
            });
            return {
                ...nextState,
                loadingState: loadingStates.fetching,
                loadingError: null,
                key: nextKey ?? nextState.key,
            };
        }
        case actionTypes.initializeStore: {
            const { config } = action.payload;
            return {
                ...state,
                config,
                loadingError: null,
                loadingState: loadingStates.success,
            };
        }
        case actionTypes.initializeError: {
            const { error } = action.payload;
            return {
                ...state,
                loadingError: error,
                loadingState: loadingStates.error,
            };
        }
        case actionTypes.setInputValue: {
            const { paramKey, value, clearOutput } = action.payload;
            const prevValue = state.input[paramKey];
            const nextValue = updateValue(value, prevValue);

            let doClear = clearOutput;
            if (clearOutput === 'auto') {
                doClear = prevValue !== nextValue;
            }

            return {
                ...state,
                input: {
                    ...state.input,
                    [paramKey]: nextValue,
                },
                fieldErrors: initial.fieldErrors,
                modelOutput: doClear ? initial.modelOutput : state.modelOutput,
                modelOutputElecCorrect: doClear
                    ? initial.modelOutputElecCorrect
                    : state.modelOutputElecCorrect,
                modelOutputFertilizer: doClear
                    ? initial.modelOutputFertilizer
                    : state.modelOutputFertilizer,
                modelOutputFertilizerBalanced: doClear
                    ? initial.modelOutputFertilizerBalanced
                    : state.modelOutputFertilizerBalanced,
            };
        }
        case actionTypes.setIonValueInput: {
            const { ion, param, value } = action.payload;
            const clearOutput = param !== 'elecCorrectRecipe';
            let nextUseDefault = state.input.useDefaultValues;

            if (param === getStreefwaardenColumn(state)) {
                nextUseDefault = false;
            }

            const nextState = reducer(
                state,
                actions.setInputValue(
                    'ionValues',
                    (prev) => ({
                        ...prev,
                        [ion]: {
                            ...prev[ion],
                            [param]: value,
                        },
                    }),
                    clearOutput
                )
            );

            return {
                ...nextState,
                input: { ...nextState.input, useDefaultValues: nextUseDefault },
            };
        }

        case actionTypes.fillInDefaultvalues: {
            let streefwaarden = getStreefwaarden(state);

            if (!streefwaarden) {
                throw new Error('Geen streefwaarden bekend');
                // return state;
            }

            const streefwaardenColumn = getStreefwaardenColumn(state);

            const nextState = reducer(
                state,
                actions.setInputValue(
                    'ionValues',
                    (prev) => {
                        const nextIonValues = Object.entries(
                            streefwaarden
                        ).reduce((ionValues, [ionKey, streefwaarde]) => {
                            return {
                                ...ionValues,
                                [ionKey]: {
                                    ...ionValues[ionKey],
                                    [streefwaardenColumn]: streefwaarde,
                                },
                            };
                        }, prev);
                        return nextIonValues;
                    },
                    true
                )
            );
            return {
                ...nextState,
                input: {
                    ...nextState.input,
                    useDefaultValues: true,
                    useDrainAnalysis: false,
                },
            };
        }

        case actionTypes.setUseDefaultvalues: {
            const nextValue = action.payload;
            if (nextValue) {
                return reducer(state, actions.fillInDefaultvalues());
            } else {
                return {
                    ...state,
                    input: {
                        ...state.input,
                        useDefaultValues: false,
                    },
                };
            }
        }

        case actionTypes.setUseDrainAnalysis: {
            const nextValue = action.payload;
            let nextState = reducer(
                state,
                actions.setInputValue('useDrainAnalysis', nextValue, 'auto')
            );
            let nextUseDefaultValues = state.input.useDefaultValues;
            if (nextValue) {
                // Defaultvalues are always for 1:1.5
                nextUseDefaultValues = false;
            }
            return {
                ...nextState,
                input: {
                    ...nextState.input,
                    useDefaultValues: nextUseDefaultValues,
                },
            };
        }

        case actionTypes.changeSelectedStage: {
            const nextStage = action.payload;
            let nextState = reducer(
                state,
                actions.setInputValue('selectedStage', nextStage, 'auto')
            );
            if (state.input.useDefaultValues) {
                nextState = reducer(nextState, actions.fillInDefaultvalues());
            }
            return { ...nextState };
        }

        case actionTypes.setModelOutput: {
            const result = action.payload;
            return copyAdviceToElcCorrect({
                ...state,
                modelOutput: updateValue(result, state.modelOutput),
            });
        }

        case actionTypes.setModelOutputElecCorrect: {
            const result = action.payload;
            return {
                ...state,
                modelOutputElecCorrect: updateValue(
                    result,
                    state.modelOutputElecCorrect
                ),
                modelOutputFertilizer: null,
                modelOutputFertilizerBalanced: null,
            };
        }
        case actionTypes.setModelOutputFertilizer: {
            const result = action.payload;
            return {
                ...state,
                modelOutputFertilizer: updateValue(
                    result,
                    state.modelOutputFertilizer
                ),
                modelOutputFertilizerBalanced: null,
            };
        }
        case actionTypes.setModelOutputFertilizerBalanced: {
            const result = action.payload;
            return {
                ...state,
                modelOutputFertilizerBalanced: updateValue(
                    result,
                    state.modelOutputFertilizerBalanced
                ),
            };
        }
        case actionTypes.setFieldErrors: {
            const errors = action.payload;
            return {
                ...state,
                fieldErrors: updateValue(errors, state.fieldErrors),
            };
        }
        case actionTypes.clearInput: {
            return {
                ...state,
                input: initial.input,
                modelOutput: initial.modelOutput,
                modelOutputElecCorrect: initial.modelOutputElecCorrect,
                modelOutputFertilizer: initial.modelOutputFertilizer,
                modelOutputFertilizerBalanced:
                    initial.modelOutputFertilizerBalanced,
                fieldErrors: initial.fieldErrors,
            };
        }
        case actionTypes.clearIonValues: {
            return {
                ...state,
                input: {
                    ...state.input,
                    ionValues: initial.input.ionValues,
                    useDefaultValues: false,
                },
            };
        }
        case actionTypes.clearOutput: {
            return {
                ...state,
                modelOutput: initial.modelOutput,
                modelOutputElecCorrect: initial.modelOutputElecCorrect,
                modelOutputFertilizer: initial.modelOutputFertilizer,
                modelOutputFertilizerBalanced:
                    initial.modelOutputFertilizerBalanced,
            };
        }
        case actionTypes.toggleShowStandardRecipe: {
            const prevOutputColumns = state.outputColumns;
            const prev = prevOutputColumns.svo.visible;
            return {
                ...state,
                outputColumns: {
                    ...prevOutputColumns,
                    svo: {
                        ...prevOutputColumns.svo,
                        visible: !prev,
                    },
                    verschil: {
                        ...prevOutputColumns.verschil,
                        visible: !prev,
                    },
                },
            };
        }
        case actionTypes.toggleElecCorrect: {
            if (state.showElecCorrect) {
                return {
                    ...state,
                    showElecCorrect: false,
                };
            }

            return {
                ...copyAdviceToElcCorrect(state),
                showElecCorrect: true,
            };
        }
        //
        case actionTypes.setFertilizerSetting: {
            const { key, nextValue } = action.payload;
            return {
                ...state,
                input: {
                    ...state.input,
                    fertilizerSettings: {
                        ...state.input.fertilizerSettings,
                        [key]: nextValue,
                    },
                },
            };
        }

        case actionTypes.setFertilizerSettings: {
            const { settings } = action.payload;
            return {
                ...state,
                input: {
                    ...state.input,
                    fertilizerSettings: {
                        ...state.input.fertilizerSettings,
                        ...settings,
                    },
                },
            };
        }

        case actionTypes.changeCropEffect: {
            const selectedCropParameter = state.input?.selectedCropParameter;
            const defaultValuesChecked = state.input.useDefaultValues;

            let selectedSubstrate = null;
            if (selectedCropParameter?.substrates?.length === 1) {
                selectedSubstrate = selectedCropParameter.substrates[0];
            }

            let nextState = state;
            if (defaultValuesChecked) {
                nextState = clearInputColumn(state, 'drainAnalysis');
            }

            return {
                ...nextState,
                input: {
                    ...nextState.input,
                    selectedSubstrate: selectedSubstrate,
                    useDefaultValues: initial.input.useDefaultValues,
                    summerWinter: initial.input.summerWinter,
                },
                fieldErrors: initial.fieldErrors,
                modelOutput: initial.modelOutput,
                modelOutputElecCorrect: initial.modelOutputElecCorrect,
                modelOutputFertilizer: initial.modelOutputFertilizer,
                modelOutputFertilizerBalanced:
                    initial.modelOutputFertilizerBalanced,
            };
        }

        case actionTypes.changeSubstrateEffect: {
            const defaultValuesChecked = state.input.useDefaultValues;

            let nextState = state;
            if (defaultValuesChecked) {
                nextState = clearInputColumn(state, 'drainAnalysis');
            }

            return {
                ...nextState,
                input: {
                    ...nextState.input,
                    useDefaultValues: initial.input.useDefaultValues,
                },
                fieldErrors: initial.fieldErrors,
                modelOutput: initial.modelOutput,
                modelOutputElecCorrect: initial.modelOutputElecCorrect,
                modelOutputFertilizer: initial.modelOutputFertilizer,
                modelOutputFertilizerBalanced:
                    initial.modelOutputFertilizerBalanced,
            };
        }

        default:
            throw new Error(`Unknown action ${action.type}`);
    }
};

export default reducer;

function copyAdviceToElcCorrect(state) {
    const prevIonValues = state.input.ionValues;
    const advice = state.modelOutput?.[outputColumns.advies];
    if (!advice) {
        throw new Error('Advies is nog niet berekend!');
        // return state;
    }
    const nextIonValues = Object.entries(prevIonValues).reduce(
        (nextIonValues, [ionKey, prevIonValue]) => {
            if (typeof prevIonValue.elecCorrectRecipe === 'undefined') {
                return {
                    ...nextIonValues,
                    [ionKey]: prevIonValue,
                };
            }
            return {
                ...nextIonValues,
                [ionKey]: {
                    ...prevIonValue,
                    elecCorrectRecipe: advice[ionKey],
                },
            };
        },
        {}
    );

    return {
        ...state,
        input: {
            ...state.input,
            ionValues: nextIonValues,
        },
    };
}

export function getStreefwaarden(state) {
    const {
        input: {
            selectedSubstrate,
            reuseDrainwaterValue,
            selectedCropParameter,
            selectedStage,
        },
        modelVersion,
    } = state;

    let streefwaarden;
    if (modelVersion === modelVersions.substraat) {
        const drainType =
            selectedSubstrate?.drainReuse && reuseDrainwaterValue?.reuse
                ? drainReuseKeys.reuse
                : drainReuseKeys.free;
        const draintypeConfig = selectedSubstrate?.draintypeConfig[drainType];
        streefwaarden = draintypeConfig?.streefwaarden;
    } else {
        // potplant
        if (
            selectedCropParameter.v_g &&
            selectedStage &&
            selectedCropParameter.streefwaarden
        ) {
            streefwaarden = selectedCropParameter.streefwaarden[selectedStage];
        } else if (!selectedCropParameter.v_g) {
            streefwaarden = selectedCropParameter.streefwaarden;
        }
    }

    return streefwaarden;
}

function getStreefwaardenColumn({ modelVersion, input: { useDrainAnalysis } }) {
    // const columnKey =
    //     modelVersion === modelVersions.potplant
    //         ? useDrainAnalysis
    //             ? 'drainAnalysis'
    //             : analysisColumnKeys.oneToOneDotFive
    //         : 'drainAnalysis';
    const columnKey =
        modelVersion === modelVersions.potplant
            ? analysisColumnKeys.oneToOneDotFive
            : 'drainAnalysis';
    return columnKey;
}

function clearInputColumn(state, columnName) {
    const nextState = reducer(
        state,
        actions.setInputValue(
            'ionValues',
            (prevIonValues) => {
                const nextIonValues = Object.entries(prevIonValues).reduce(
                    (ionValues, [ionKey, currentValues]) => {
                        return {
                            ...ionValues,
                            [ionKey]: {
                                ...currentValues,
                                [columnName]: '',
                            },
                        };
                    },
                    prevIonValues
                );
                return nextIonValues;
            },
            true
        )
    );
    return nextState;
}
